import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { generatePDFLabels } from './print//BarrelTagPrint';
import QuickAddVineyardModal from '../components/QuickAddVineyardModal.js';
import LogEntries from '../components/LogEntries.js';
import WineToolbar from '../components/toolbars/WineToolbar.js';
import { TagManager } from '../components/WineTags';
import { ClipboardX, PackageX } from 'lucide-react';
import '../index.css';

const WineDetail = () => {
    const { wineID } = useParams();
    const navigate = useNavigate();
    const [currentUser, setCurrentUser] = useState(null);
    const [isEditingContainer, setIsEditingContainer] = useState(false);
    const [wine, setWine] = useState(null);
    const [containers, setContainers] = useState([]);
    const [availableContainers, setAvailableContainers] = useState([]); 
    const [selectedContainer, setSelectedContainer] = useState(null);
    const [newContainerGallons, setNewContainerGallons] = useState(''); 
    const [tagRefreshCounter, setTagRefreshCounter] = useState(0);
    const refreshTags = () => setTagRefreshCounter(prevCounter => prevCounter + 1);  
    const [analysis, setAnalysis] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [vineyards, setVineyards] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [showQuickAddVineyard, setShowQuickAddVineyard] = useState(false);
    const [varietals, setVarietals] = useState([]);
    const [vintages, setVintages] = useState([]);
    const [showAnalysisModal, setShowAnalysisModal] = useState(false);
    const [logs, setLogs] = useState([]);
    const [modalAnalysis, setModalAnalysis] = useState({
        anDate: '',
        brix: '',
        pH: '',
        TA: '',
        F_SO2: '',
        T_SO2: '',
        ETOH: '',
        RS: '',
        VA: '',
        malic: '',
        YAN: '',
        anNotes: '',
        ammonia: '',
        fructose: '',
        glucose: '',
        molecularSO2: '',
        NOPA: '',
        potassium: '',
        labID: ''
    });
    const [editedWine, setEditedWine] = useState({
        varietal: '',
        vintage: '',
        grower: '',
        appellation: '',
        vineyard: '',
        wineryID: '',
    });
    const [showContainerModal, setShowContainerModal] = useState(false);

    // Generate vintages
    const generateVintages = () => {
        const currentYear = new Date().getFullYear();
        const years = ['Non-Vintage'];
        for (let i = currentYear; i >= currentYear - 12; i--) {
            years.push(i.toString());
        }
        return years;
    };

    // Initial data fetch
    useEffect(() => {
        const fetchAllData = async () => {
            if (!wineID || isNaN(wineID)) {
                setError('Invalid wine ID');
                return;
            }

            try {
                setIsLoading(true);
                
                // Fetch all required data in parallel
                const [
                    wineResponse, 
                    containersResponse, 
                    analysisResponse,
                    vineyardsResponse,
                    varietalsResponse,
                companiesResponse
                ] = await Promise.all([
                    axios.get(`/api/wines/${wineID}`),
                    axios.get(`/api/containers/${wineID}`),
                    axios.get(`/api/analysis/${wineID}`),
                    axios.get('/api/vineyards'),
                    axios.get('/api/varietals'),
                axios.get('/api/companies?type=grower')
                ]);

                setWine(wineResponse.data);
                setContainers(containersResponse.data);
                setAnalysis(analysisResponse.data);
                setVineyards(vineyardsResponse.data);
                setVarietals(varietalsResponse.data);
                setCompanies(companiesResponse.data);
                setVintages(generateVintages());

                // Pre-fill edit form
                setEditedWine({
                    varietal: wineResponse.data.varietal,
                    vintage: wineResponse.data.vintage,
                    grower: wineResponse.data.grower,
                    appellation: wineResponse.data.appellation,
                    vineyard: wineResponse.data.vineyard,
                    totalGallons: wineResponse.data.totalGallons,
                    finalAlc: wineResponse.data.finalAlc,
                    wineIDvisual: wineResponse.data.wineIDvisual,
                    wineNotes: wineResponse.data.wineNotes,
                });

                setIsLoading(false);
            } catch (err) {
                console.error('Error fetching data:', err);
                setError('Failed to load wine details');
                setIsLoading(false);
            }
        };

        fetchAllData();
    }, [wineID]);
    const fetchAvailableContainers = async () => {
        try {
            const response = await axios.get('/api/containers/available');
            setAvailableContainers(response.data);
        } catch (error) {
            console.error('Error fetching available containers:', error);
            setError('Failed to load available containers');
        }
    };
    
    const openAddAnalysisModal = () => {
        setModalAnalysis({
            anDate: '',
            brix: '',
            pH: '',
            TA: '',
            F_SO2: '',
            T_SO2: '',
            ETOH: '',
            RS: '',
            VA: '',
            malic: '',
            YAN: '',
            anNotes: '',
            ammonia: '',
            fructose: '',
            glucose: '',
            molecularSO2: '',
            NOPA: '',
            potassium: '',
            labID: ''
        });
        setShowAnalysisModal(true);
    };

    const openEditAnalysisModal = (analysis) => {
        setModalAnalysis(analysis);
        setShowAnalysisModal(true);
    };
    //####### Add for logs
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [wineResponse, logsResponse, userResponse] = await Promise.all([
                    axios.get(`/api/wines/${wineID}`),
                    axios.get(`/api/logs/${wineID}/wine`),
                    axios.get('/api/auth/me')  // Endpoint to get current user info
                ]);
                setWine(wineResponse.data);
                setLogs(logsResponse.data);
                setCurrentUser(userResponse.data);
            } catch (error) {
                console.error('Error fetching data:', error);
                setError('Failed to load wine details');
            }
        };
        fetchData();
    }, [wineID]);
    
    // Add handler for new logs
    const handleAddLog = async (logData) => {
        try {
            // Convert entityId to number if it's a string
            const payload = {
                ...logData,
                entityId: Number(logData.entityId),
                // Don't include userName here as it will be set by the server
            };
            
            const response = await axios.post('/api/logs', payload);
            
            // Update the logs state with the response data
            setLogs(prevLogs => [response.data, ...prevLogs]);
        } catch (error) {
            console.error('Error adding log:', error);
            setError('Failed to add note');
        }
    };
    //###### Delete Handle for Logs
    const handleDeleteLog = async (logId) => {
        if (window.confirm('Are you sure you want to delete this note?')) {
            try {
                await axios.delete(`/api/logs/${logId}`);
                setLogs(prevLogs => prevLogs.filter(log => log.id !== logId));
            } catch (error) {
                console.error('Error deleting log:', error);
                setError('Failed to delete note');
            }
        }
    };
    const handleFieldChange = (e) => {
        const { name, value } = e.target;
        
        // Special handling for vineyard selection
        if (name === 'vineyard') {
            const selectedVineyard = vineyards.find(v => v.vineyardName === value);
            if (selectedVineyard) {
                // Find the associated company/grower
                const grower = companies.find(c => c.id === selectedVineyard.companyID);
                const growerName = grower ? grower.companyName : '';
    
                setEditedWine(prev => ({
                    ...prev,
                    vineyard: value,
                    grower: growerName,
                    appellation: selectedVineyard.vinAppellation || prev.appellation
                }));
            } else {
                // Reset vineyard-related fields if "None" is selected
                setEditedWine(prev => ({
                    ...prev,
                    vineyard: '',
                    grower: prev.grower, // Keep existing grower info
                    appellation: prev.appellation // Keep existing appellation
                }));
            }
        } else {
            setEditedWine(prev => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    const handleEdit = () => {
        setEditedWine({
            varietal: wine.varietal,
            vintage: wine.vintage,
            grower: wine.grower,
            appellation: wine.appellation,
            vineyard: wine.vineyard,
            totalGallons: wine.totalGallons,
            finalAlc: wine.finalAlc,
            wineIDvisual: wine.wineIDvisual,
            wineNotes: wine.wineNotes,
        });
        setIsEditing(true);
    };

    const handleDelete = () => {
        if (window.confirm('Are you sure you want to delete this wine lot?')) {
            axios.delete(`/api/wines/${wineID}`)
                .then(() => {
                    navigate('/wines');
                })
                .catch((error) => {
                    console.error('Error deleting wine:', error);
                    setError('Failed to delete wine lot.');
                });
        }
    };

    const toggleActiveStatus = () => {
        const action = wine.active ? 'archive' : 'unarchive';
        if (window.confirm(`Are you sure you want to ${action} this wine lot?`)) {
            axios.put(`/api/wines/${wineID}/toggle-active`)
                .then(() => {
                    return axios.get(`/api/wines/${wineID}`);
                })
                .then((response) => {
                    setWine(response.data);
                })
                .catch((error) => {
                    console.error(`Error toggling active status:`, error);
                    setError('Failed to toggle active status.');
                });
        }
    };
    const handleShowContainerModal = async () => {
        await fetchAvailableContainers();
        setShowContainerModal(true);
    };

    const handleContainerRowClick = (container) => {
        setSelectedContainer(container.containerID);
        setNewContainerGallons(container.cntGallons);
        setIsEditingContainer(true);
        setShowContainerModal(true);
    };

    const handleRemoveContainer = async (containerID) => {
        if (window.confirm('Are you sure you want to remove this container from the wine?')) {
            try {
                await axios.put(`/api/containers/${containerID}/remove`);
                setContainers(containers.filter((container) => container.containerID !== containerID));
            } catch (error) {
                console.error('Error removing container:', error);
                setError('Failed to remove container.');
            }
        }
    };

    const handleAddContainer = async (e) => {
        e.preventDefault();
        if (!selectedContainer || !newContainerGallons) {
            alert('Please select a container and specify gallons.');
            return;
        }
        try {
            await axios.put(`/api/containers/${selectedContainer}/assign`, {
                wineID: wine.wineID,
                cntGallons: newContainerGallons,
            });

            const updatedContainers = await axios.get(`/api/containers/${wine.wineID}`);
            setContainers(updatedContainers.data);

            setShowContainerModal(false);
            setSelectedContainer(null);
            setNewContainerGallons('');
        } catch (error) {
            console.error('Error assigning container:', error);
            setError('Failed to assign container.');
        }
    };

    const handleUpdateContainer = async (e) => {
        e.preventDefault();
        try {
            await axios.put(`/api/containers/${selectedContainer}`, {
                cntGallons: newContainerGallons
            });

            const updatedContainers = await axios.get(`/api/containers/${wine.wineID}`);
            setContainers(updatedContainers.data);

            setShowContainerModal(false);
            setSelectedContainer(null);
            setNewContainerGallons('');
            setIsEditingContainer(false);
        } catch (error) {
            console.error('Error updating container:', error);
            setError('Failed to update container.');
        }
    };

    const handleSaveEdit = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.put(`/api/wines/${wineID}`, editedWine);
            setWine(response.data);
            setIsEditing(false);
        } catch (error) {
            console.error('Error saving wine details:', error);
            setError('Failed to save wine details');
        }
    };

    const modalTitle = isEditingContainer ? 'Edit Container' : 'Add New Container';
    
    const handleModalSubmit = isEditingContainer ? handleUpdateContainer : handleAddContainer;

    const handleSaveAnalysis = async (e) => {
        e.preventDefault();
        const analysisData = {
            wineID: wine.wineID,
            anDate: modalAnalysis.anDate,
            brix: modalAnalysis.brix,
            temp: modalAnalysis.temp,
            pH: modalAnalysis.pH,
            TA: modalAnalysis.TA,
            F_SO2: modalAnalysis.F_SO2,
            T_SO2: modalAnalysis.T_SO2,
            ETOH: modalAnalysis.ETOH,
            RS: modalAnalysis.RS,
            VA: modalAnalysis.VA,
            malic: modalAnalysis.malic,
            YAN: modalAnalysis.YAN,
            anNotes: modalAnalysis.anNotes,
            ammonia: modalAnalysis.ammonia,
            fructose: modalAnalysis.fructose,
            glucose: modalAnalysis.glucose,
            molecularSO2: modalAnalysis.molecularSO2,
            NOPA: modalAnalysis.NOPA,
            potassium: modalAnalysis.potassium,
            labID: modalAnalysis.labID
        };

        try {
            if (modalAnalysis.analysisID) {
                await axios.put(`/api/analysis/${modalAnalysis.analysisID}`, analysisData);
                setAnalysis((prev) =>
                    prev.map((item) =>
                        item.analysisID === modalAnalysis.analysisID ? modalAnalysis : item
                    )
                );
                alert('Analysis updated successfully!');
            } else {
                const response = await axios.post('/api/analysis', analysisData);
                setAnalysis((prev) => [...prev, response.data]);
                alert('Analysis added successfully!');
            }
            setShowAnalysisModal(false);
        } catch (error) {
            console.error('Error saving analysis:', error);
            alert(`Failed to ${modalAnalysis.analysisID ? 'update' : 'add'} analysis.`);
        }
    };
    //##### Manual Marked Topped ######
    const handleMarkTopped = async () => {
        try {
          // Find and remove the "Needs Topping" tag if it exists
          const response = await axios.get(`/api/wines/${wineID}/tags`);
          const needsToppingTag = response.data.find(tag => tag.name === "Needs Topping");
          
          if (needsToppingTag) {
            await axios.delete(`/api/wines/${wineID}/tags/${needsToppingTag.tagID}`);
            
            // Update lastTopped date
            await axios.put(`/api/wines/${wineID}/topped`, {
              lastTopped: new Date().toISOString().split('T')[0]
            });
            
            // Add log entry for topping
            const logData = {
              entityId: parseInt(wineID),
              entityType: "wine",
              content: "Wine topped up"
            };
            await handleAddLog(logData);
            
            // Refresh tags
            refreshTags();
            
            // Get updated wine data
            const wineResponse = await axios.get(`/api/wines/${wineID}`);
            setWine(wineResponse.data);
          }
        } catch (error) {
          console.error('Error marking wine as topped:', error);
          setError('Failed to mark wine as topped');
        }
      };

//########### Display loading, error, or content
    if (isLoading) { return <p>Loading...</p>;}
    if (error) { return <p>{error}</p>; }

    return (
        <>
            <WineToolbar 
                wine={wine}
                onEdit={handleEdit}
                onShowContainer={handleShowContainerModal}  // Modified this line
                onShowAnalysis={openAddAnalysisModal}
                onPrintLabels={() => generatePDFLabels(containers, wine)}
                onToggleActive={toggleActiveStatus}
                onDelete={handleDelete}
                onMarkTopped={handleMarkTopped} 
            />
        <div className="container mt-4">
            <div className="d-flex justify-content-between align-items-center">
                <h3>{wine.varietal} {wine.vintage}</h3>
                <strong>Lot ID:{wine.wineID}</strong>
            </div>
            <div>
                
            </div>
            {isEditing ? (
                <form onSubmit={handleSaveEdit}>
                    <div className="row">
                    <div className="col-md-6 mb-3">
                            <label className="form-label">Vintage</label>
                            <select
                                className="form-control"
                                name="vintage"
                                value={editedWine.vintage || ''}
                                onChange={handleFieldChange}
                            >
                                <option value="">Select Vintage</option>
                                {vintages.map((vintage, index) => (
                                    <option key={index} value={vintage}>
                                        {vintage}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Varietal</label>
                            <select
                                className="form-control"
                                name="varietal"
                                value={editedWine.varietal || ''}
                                onChange={handleFieldChange}
                            >
                                <option value="">Select Varietal</option>
                                {varietals.map((varietal, index) => (
                                    <option key={index} value={varietal}>
                                        {varietal}
                                    </option>
                                ))}
                            </select>
                            <div className="mt-2">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => setShowQuickAddVineyard(true)}
                                >
                                    + Quick Add Vineyard
                                </button>
                            </div>
                        </div>

                        {/* Quick Add Vineyard Modal */}
                        <QuickAddVineyardModal
                            show={showQuickAddVineyard}
                            onClose={() => setShowQuickAddVineyard(false)}
                            companies={companies}
                            onSave={async (newVineyard) => {
                                try {
                                    const response = await axios.post('/api/vineyards', newVineyard);
                                    const updatedVineyardsRes = await axios.get('/api/vineyards');
                                    setVineyards(updatedVineyardsRes.data);
                                    
                                    // Automatically select the newly created vineyard
                                    handleFieldChange({
                                        target: {
                                            name: 'vineyardID',
                                            value: response.data.id.toString()
                                        }
                                    });
                                    return response.data;
                                } catch (error) {
                                    console.error('Error creating vineyard:', error);
                                    throw error;
                                }
                            }}
                        />
                     <div className="col-md-6 mb-3">
                         <label className="form-label">Vineyard</label>
                            <select
                                className="form-control"
                                name="vineyard"
                                value={editedWine.vineyard}
                                onChange={handleFieldChange}
                            >
                                <option value="">Select Vineyard</option>
                                {vineyards.map((vineyard) => (
                                    <option key={vineyard.id} value={vineyard.vineyardName}>
                                        {vineyard.vineyardName}
                                    </option>
                                ))}
                            </select>
                     </div>
                     <div className="col-md-6 mb-3">
                         <label className="form-label">Grower</label>
                            <input
                                className="form-control"
                                type="text"
                                name="grower"
                                value={editedWine.grower}
                                onChange={handleFieldChange}
                                disabled
                            />
                     </div>
                     <div className="col-md-6 mb-3">
                         <label className="form-label">Total Gallons</label>
                         <input
                             type="number"
                             className="form-control"
                             name="totalGallons"
                             value={editedWine.totalGallons || ''}
                             onChange={handleFieldChange}
                         />
                     </div>
                     <div className="col-md-6 mb-3">
                         <label className="form-label">Final Alcohol (%)</label>
                         <input
                             type="number"
                             className="form-control"
                             name="finalAlc"
                             value={editedWine.finalAlc || ''}
                             onChange={handleFieldChange}
                         />
                     </div>
                     <div className="col-md-6 mb-3">
                         <label className="form-label">Descriptive Wine ID</label>
                         <input
                             type="text"
                             className="form-control"
                             name="wineIDvisual"
                             value={editedWine.wineIDvisual || ''}
                             onChange={handleFieldChange}
                         />
                           <div className="mb-4">
                                <h5>Tags</h5>
                                <TagManager 
                                wineID={wineID} 
                                refreshTags={tagRefreshCounter}
                                />
                            </div>
                     </div>
                 </div>
                 <div className="btn-group">
                 <button type="submit" className="btn btn-sm btn-outline-primary">Save Changes</button>
                 <button
                     type="button"
                     className="btn btn-sm btn-outline-secondary"
                     onClick={() => setIsEditing(false)}
                 >
                     Cancel
                 </button>
                 </div>
             </form>
            ) : (
                <>
                <div className="row" 
                    onClick={handleEdit}
                    style={{ cursor: 'pointer' }}
                    title="Click to edit wine details"
                    >
                    <div className="col-md-6 mb-3">
                        <strong>ID:</strong> {wine.wineIDvisual}
                    </div>
                    <div className="col-md-6 mb-3">
                        <strong>Appellation:</strong> {wine.appellation}
                    </div>
                    <div className="col-md-6 mb-3">
                        <strong>Grower:</strong> {wine.grower}
                    </div>
                    <div className="col-md-6 mb-3">
                        <strong>Vineyard:</strong> {wine.vineyard}
                    </div>
                    <div className="col-md-6 mb-3">
                        <strong>Wine Style:</strong> {wine.varietalDetail}
                    </div>
                    <div className="col-md-6 mb-3">
                        <strong>Gallons:</strong> {wine.totalGallons}
                    </div>
                    <div className="col-md-6 mb-3">
                        <strong>Alcohol:</strong> {wine.finalAlc}%
                    </div>
                    <style>
                        {`
                            .row:hover {
                                background-color: rgba(0,0,0,0.05);
                                transition: background-color 0.2s ease;
                            }
                        `}
                    </style>

                </div>
                    <div className="col-md-12 mb-3">
                        <LogEntries
                            entityId={Number(wineID)}
                            entityType="wine"
                            logs={logs}
                            onAddLog={handleAddLog}
                            onDeleteLog={handleDeleteLog}
                            currentUser={currentUser}
                        />
                    </div>
                </>
            )}

            {/* Related Containers Table */}
        {containers.length > 0 && (
            <>
            <h4>Barrels & Tanks</h4>
            <div className="table-responsive">
            <table className="table table-striped table-hover">
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Description</th>
                        <th>Gal.</th>
                        <th>Remove</th>
                    </tr>
                </thead>
                <tbody>
                    {containers.map(container => (
                        <tr 
                            key={container.containerID}
                            onClick={() => handleContainerRowClick(container)}
                            style={{ cursor: 'pointer' }}
                            className="table-row-hover"
                        >
                            <td>{container.containerID}</td>
                            <td>{container.cntDesc}</td>
                            <td>{container.cntGallons}</td>
                            <td className="text-end">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveContainer(container.containerID);
                                    }}
                                    title="Remove Container"
                                >
                                    <PackageX size={18} />
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            </div>
            </>
        )}
			{/* Display Related Analysis Data */}
    {analysis.length > 0 && (
    <>
		<h4>Analysis</h4>
        <div className="table-responsive">
		<table className="table table-striped table-hover">
			<thead>
				<tr>
					<th>Date</th>
					<th>pH</th>
					<th>TA</th>
					<th>FSO2</th>
					<th>ETOH</th>
					<th>RS</th>
					<th>VA</th>
                    <th>Actions</th>
				</tr>
			</thead>
			<tbody>
				{analysis.map((item) => (
					<tr 
                    key={item.analysisID}
                    onClick={() => openEditAnalysisModal(item)}
                    style={{ cursor: 'pointer' }}
                    className="table-row-hover"
                    >
						<td>{item.anDate || 'N/A'}</td>
						<td>{item.pH || 'N/A'}</td>
						<td>{item.TA || 'N/A'}</td>
						<td>{item.F_SO2 || 'N/A'}</td>
						<td>{item.ETOH || 'N/A'}</td>
						<td>{item.RS || 'N/A'}</td>
						<td>{item.VA || 'N/A'}</td>
                        <td>
                            <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm('Are you sure you want to delete this analysis?')) {
                                axios.delete(`/api/analysis/${item.analysisID}`)
                                    .then(() => {
                                    setAnalysis(prevAnalysis => 
                                        prevAnalysis.filter(a => a.analysisID !== item.analysisID)
                                    );
                                    })
                                    .catch(error => {
                                    console.error('Error deleting analysis:', error);
                                    setError('Failed to delete analysis');
                                    });
                                }
                            }}
                            >
                            <ClipboardX size={18} />
                            </button>
                        </td>
					</tr>
				))}
			</tbody>
		</table>
        <style>
            {`
                .table-row-hover:hover td {
                    background-color: rgba(0,0,0,.075);
                }
                .table-row-hover {
                    transition: background-color 0.2s ease;
                }
            `}
        </style>
        </div>
        </>
        )}
            {/* Container Modal */}
            {showContainerModal && (
                <>
                <div className="modal-backdrop fade show"></div>
                <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{modalTitle}</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    aria-label="Close"
                                    onClick={() => {
                                        setShowContainerModal(false);
                                        setIsEditingContainer(false);
                                        setSelectedContainer(null);
                                        setNewContainerGallons('');
                                    }}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <form onSubmit={handleModalSubmit}>
                                    {!isEditingContainer && (
                                        <div className="mb-3">
                                            <label className="form-label">Select Container</label>
                                            <select
                                                className="form-control"
                                                value={selectedContainer || ''}
                                                onChange={(e) => setSelectedContainer(e.target.value)}
                                            >
                                                <option value="" disabled>Select a container</option>
                                                {availableContainers.map((container) => (
                                                    <option key={container.containerID} value={container.containerID}>
                                                        {container.containerID} - {container.cntDesc}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    <div className="mb-3">
                                        <label className="form-label">Gallons</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={newContainerGallons}
                                            onChange={(e) => setNewContainerGallons(e.target.value)}
                                            placeholder="Enter gallons"
                                        />
                                    </div>
                                    <button type="submit" className="btn btn-success">
                                        {isEditingContainer ? 'Update Container' : 'Add Container'}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
                </>
            )}

                {/* Edit Analysis Modal */}
                {showAnalysisModal && (
                    <>
                    {/* Modal Backdrop */}
                    <div className="modal-backdrop fade show"></div>
                    <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">
                                        {modalAnalysis.analysisID ? 'Edit Analysis' : 'Add New Analysis'}
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={() => setShowAnalysisModal(false)}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                <form onSubmit={handleSaveAnalysis}>
                    <div className="mb-3">
                        <label className="form-label">Date</label>
                        <input
                            type="date"
                            className="form-control"
                            value={modalAnalysis.anDate || ''}
                            onChange={(e) => setModalAnalysis({ ...modalAnalysis, anDate: e.target.value })}
                        />
                    </div>
                    <div className="row">
                        <div className="col-md-4 mb-3">
                            <label className="form-label">Brix</label>
                            <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                value={modalAnalysis.brix || ''}
                                onChange={(e) => setModalAnalysis({ ...modalAnalysis, brix: e.target.value })}
                            />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label">Temperature</label>
                            <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                value={modalAnalysis.temp || ''}
                                onChange={(e) => setModalAnalysis({ ...modalAnalysis, temp: e.target.value })}
                            />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label">pH</label>
                            <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                value={modalAnalysis.pH || ''}
                                onChange={(e) => setModalAnalysis({ ...modalAnalysis, pH: e.target.value })}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-4 mb-3">
                            <label className="form-label">TA</label>
                            <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                value={modalAnalysis.TA || ''}
                                onChange={(e) => setModalAnalysis({ ...modalAnalysis, TA: e.target.value })}
                            />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label">Free SO₂</label>
                            <input
                                type="number"
                                className="form-control"
                                value={modalAnalysis.F_SO2 || ''}
                                onChange={(e) => setModalAnalysis({ ...modalAnalysis, F_SO2: e.target.value })}
                            />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label">Total SO₂</label>
                            <input
                                type="number"
                                className="form-control"
                                value={modalAnalysis.T_SO2 || ''}
                                onChange={(e) => setModalAnalysis({ ...modalAnalysis, T_SO2: e.target.value })}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-4 mb-3">
                            <label className="form-label">ETOH</label>
                            <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                value={modalAnalysis.ETOH || ''}
                                onChange={(e) => setModalAnalysis({ ...modalAnalysis, ETOH: e.target.value })}
                            />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label">RS</label>
                            <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                value={modalAnalysis.RS || ''}
                                onChange={(e) => setModalAnalysis({ ...modalAnalysis, RS: e.target.value })}
                            />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label">VA</label>
                            <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                value={modalAnalysis.VA || ''}
                                onChange={(e) => setModalAnalysis({ ...modalAnalysis, VA: e.target.value })}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-4 mb-3">
                            <label className="form-label">Malic Acid</label>
                            <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                value={modalAnalysis.malic || ''}
                                onChange={(e) => setModalAnalysis({ ...modalAnalysis, malic: e.target.value })}
                            />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label">YAN</label>
                            <input
                                type="number"
                                className="form-control"
                                value={modalAnalysis.YAN || ''}
                                onChange={(e) => setModalAnalysis({ ...modalAnalysis, YAN: e.target.value })}
                            />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label">Ammonia</label>
                            <input
                                type="number"
                                className="form-control"
                                value={modalAnalysis.ammonia || ''}
                                onChange={(e) => setModalAnalysis({ ...modalAnalysis, ammonia: e.target.value })}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-4 mb-3">
                            <label className="form-label">Fructose</label>
                            <input
                                type="number"
                                className="form-control"
                                value={modalAnalysis.fructose || ''}
                                onChange={(e) => setModalAnalysis({ ...modalAnalysis, fructose: e.target.value })}
                            />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label">Glucose</label>
                            <input
                                type="number"
                                className="form-control"
                                value={modalAnalysis.glucose || ''}
                                onChange={(e) => setModalAnalysis({ ...modalAnalysis, glucose: e.target.value })}
                            />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label">Molecular SO₂</label>
                            <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                value={modalAnalysis.molecularSO2 || ''}
                                onChange={(e) => setModalAnalysis({ ...modalAnalysis, molecularSO2: e.target.value })}
                            />
                            </div>
                    </div>
                    <div className="row">
                        <div className="col-md-4 mb-3">
                            <label className="form-label">NOPA</label>
                            <input
                                type="number"
                                className="form-control"
                                value={modalAnalysis.NOPA || ''}
                                onChange={(e) => setModalAnalysis({ ...modalAnalysis, NOPA: e.target.value })}
                            />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label">Potassium</label>
                            <input
                                type="number"
                                className="form-control"
                                value={modalAnalysis.potassium || ''}
                                onChange={(e) => setModalAnalysis({ ...modalAnalysis, potassium: e.target.value })}
                            />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label">Lab ID</label>
                            <input
                                type="text"
                                className="form-control"
                                value={modalAnalysis.labID || ''}
                                onChange={(e) => setModalAnalysis({ ...modalAnalysis, labID: e.target.value })}
                            />
                            </div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Analysis Notes</label>
                        <textarea
                            className="form-control"
                            rows="3"
                            value={modalAnalysis.anNotes || ''}
                            onChange={(e) => setModalAnalysis({ ...modalAnalysis, anNotes: e.target.value })}
                        ></textarea>
                    </div>
                    <button type="submit" className="btn btn-success">
                        {modalAnalysis.analysisID ? 'Save Changes' : 'Add Analysis'}
                    </button>
                </form>
                </div>
            </div>
        </div>
    </div>
    </>
)}
        </div>
        </>
    );
};

export default WineDetail;